import React from "react";
import { motion } from "framer-motion";
import { GROWTH_OS, ADDITIONAL_SERVICES } from "../data/entrepminds";

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#f3eedf]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="section-eyebrow mb-4">The GrowthOS™ Framework</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
            Five stages.{" "}
            <span className="gold-text">One operating system</span> for growth.
          </h2>
          <p className="mt-6 text-[#6b6356] text-base md:text-lg leading-relaxed">
            From visibility to scale, every layer is wired together — software,
            automation, AI and performance marketing operating as a single engine.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative mt-20">
          <div className="timeline-rail hidden md:block" />

          <div className="space-y-12 md:space-y-20">
            {GROWTH_OS.map((stage, idx) => {
              const Icon = stage.Icon;
              const isLeft = idx % 2 === 0;
              return (
                <motion.div
                  key={stage.code}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: idx * 0.05 }}
                  data-testid={`growthos-stage-${stage.code}`}
                  className="relative md:grid md:grid-cols-2 md:gap-16 items-center"
                >
                  {/* node */}
                  <div className="absolute left-[16px] md:left-1/2 md:-translate-x-1/2 top-2 md:top-1/2 md:-translate-y-1/2 w-4 h-4 rounded-full bg-[#d4af37] em-glow z-10" />

                  <div
                    className={`pl-12 md:pl-0 ${
                      isLeft ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"
                    }`}
                  >
                    <div className="em-card p-7 inline-block w-full">
                      <div
                        className={`flex items-center gap-4 mb-4 ${
                          isLeft ? "md:justify-end" : ""
                        }`}
                      >
                        <div className="font-mono-em text-xs text-[#8a6a10] tracking-[0.25em]">
                          {stage.code}
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/45 flex items-center justify-center">
                          <Icon size={18} className="text-[#8a6a10]" />
                        </div>
                      </div>
                      <h3 className="font-display text-3xl font-semibold mb-2">
                        {stage.title}
                      </h3>
                      <p className="text-[#6b6356] text-sm mb-5">{stage.tagline}</p>
                      <ul
                        className={`space-y-2 text-sm text-[#3f3a30] ${
                          isLeft ? "md:text-right" : ""
                        }`}
                      >
                        {stage.items.map((it) => (
                          <li key={it} className="flex items-center gap-2 md:gap-3">
                            <span
                              className={`w-1.5 h-1.5 rounded-full bg-[#d4af37] ${
                                isLeft ? "md:order-2" : ""
                              }`}
                            />
                            <span className={isLeft ? "md:order-1 md:flex-1" : ""}>
                              {it}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div
                    className={`hidden md:block ${
                      isLeft ? "md:col-start-2" : "md:col-start-1 md:row-start-1"
                    }`}
                  >
                    <div className="relative aspect-square rounded-2xl overflow-hidden border border-[#d4af37]/35">
                      <img
                        src={stage.image}
                        alt={stage.imageAlt || stage.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0a0a0a]/45 via-transparent to-[#d4af37]/15" />
                      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                        <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-white/90 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/15">
                          STAGE {stage.code}
                        </div>
                      </div>
                      <div className="absolute bottom-5 left-5 right-5">
                        <div className="font-display text-2xl text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                          {stage.title}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Additional services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-28"
        >
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="section-eyebrow mb-3">À la carte</div>
              <h3 className="font-display text-3xl md:text-4xl">
                Additional <span className="gold-text">specialist services</span>
              </h3>
            </div>
            <div className="text-sm text-[#6b6356] max-w-sm">
              Standalone engagements when you need surgical execution — not a full
              transformation.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADDITIONAL_SERVICES.map((s) => {
              const Icon = s.Icon;
              return (
                <div
                  key={s.name}
                  data-testid={`addon-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="em-card p-6 flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/45 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-[#8a6a10]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#0a0a0a]">{s.name}</div>
                    <div className="font-mono-em text-xs text-[#8a6a10] mt-1">
                      {s.range}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
